import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, interval } from "rxjs";
import {
  debounce,
  distinctUntilChanged,
  filter,
  map,
  tap,
} from "rxjs/operators";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { Card } from "src/model/class/general classes/card";
import {
  Environment,
  EnvironmentTree,
} from "src/components/shared-components/class/general-classes/environment";
import { GroupTimeSlot } from "src/model/class/general classes/group-time-slot";
import { PlantCodeABB } from "src/model/class/general classes/plant-code-abb";

import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import {
  ApplicationTheme,
  ToastType,
} from "src/components/shared-components/enum/enumMisc";
import { AlertService } from "src/service/alert.service";
import { EnvironmentService } from "src/service/environment.service";

import { ThemeService } from "src/service/theme.service";
import { ToastService } from "src/service/toast.service";
import { EnvironmentCategoryReverse } from "src/model/interface/environment-category-reverse";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { LoaderService } from "src/service/loader.service";
import { ActivatedRoute } from "@angular/router";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { Store } from "@ngrx/store";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { SystemRouterService } from "src/service/system-router.service";
import { PermissionService } from "src/service/permission.service";
import { LevelProtection } from "src/model/enum/enumDigitalPlace";

@Component({
    selector: "app-form-edit-environment",
    templateUrl: "./form-edit-environment.component.html",
    styleUrls: ["./form-edit-environment.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormEditEnvironmentComponent implements OnInit, OnDestroy {
  private _logContext: string = "form-edit-environment.component";

  private _translateSubscription: Subscription = null;
  private _idArea: number = null;
  private _currentUser: CurrentSessionData = null;
  private _selectCurrentUserSub: Subscription = null;
  private _lblSaveSuccess: string = "";
  private _lblCompileAllFields: string = "";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsSub: Subscription = null;
  //#endregion

  //#region Translated string

  //#endregion

  public formEditEnvironment: UntypedFormGroup = null;
  public listGroupTimeSlot: Array<GroupTimeSlot> = null;
  public filterListGroupTimeSlot: Array<GroupTimeSlot> =
    new Array<GroupTimeSlot>();
  public listCardTemplate: Array<Card> = null;
  public mapCardType: Map<number, string> = new Map();

  public _eventSubscriptions: Array<Subscription> = null;
  public copyDescription: boolean = true;
  public startDateBAckground: string = "";
  public endDateBackground: string = "";
  public alreadyUpdated: boolean = false;
  public hideDataExpiration: boolean = false;
  public listEnvironmentCategory: Array<EnvironmentCategoryReverse>;
  public listABBPlantCodes: Array<PlantCodeABB> = null;
  public isAppartament: boolean = false;
  public disableCategory: boolean = false;

  @Input("currentEnvironment") currentEnvironment: Environment;
  @Input() envHasDevicesAndChildren: boolean;
  @Input() isEditMode: boolean;

  @Output() resetEnvironment: EventEmitter<void> = new EventEmitter();

  constructor(
    private _fb: UntypedFormBuilder,
    private _translateService: TranslateService,
    private _themeService: ThemeService,
    private _environmentService: EnvironmentService,
    private _alertService: AlertService,
    private _loaderService: LoaderService,
    private _route: ActivatedRoute,
    private _store: Store,
    private _systemRouterService: SystemRouterService,
    private _permissionService: PermissionService
  ) { }

  /**
   * ngOnInit, reload supervision
   */
  public async ngOnInit(): Promise<void> {
    if (this._route.snapshot.params.idArea) {
      this._idArea = this._route.snapshot.params.idArea;
    }
    this.loadCurrentUserData();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this._environmentService.editEnvironment.subscribe(async value => {
      if (!this.isEditMode) {
        this.getCategoriesForNewEnvironments(value);
      }
    })
  }

  /**
   * Add environment constraints for dropdown menu according to parent category
   * @param idParent 
   */
  private async getEnvironmentConstraintsForDropdownMenu(idParent: number): Promise<void> {
    const environmentParent = await this._environmentService
      .getEnvironments(null, idParent, false)
      .toPromise();

    // Filtering category list with constraints for building parts
    if (environmentParent.data[0]) {
      this.listEnvironmentCategory = this.listEnvironmentCategory.filter(
        (listElement: EnvironmentCategoryReverse) =>
          this._environmentService.addEnvironmentConstraints(
            listElement,
            environmentParent.data[0].category
          )
      );
    }
  }

  /**
   * Handle thousandths
   */
  public handleThousandths() {
    if (
      this.formEditEnvironment?.get("category").value ==
      EnvironmentCategory.APARTAMENT
    ) {
      this.isAppartament = true;
    } else {
      this.isAppartament = false;
    }
  }

  /**
   * Check if sub field is visible
   * @returns true if category is apartament, garage, common area
   */
  public isSubFieldVisible(): boolean {
    if (
      this.formEditEnvironment?.get("category").value ==
      EnvironmentCategory.APARTAMENT || this.formEditEnvironment?.get("category").value ==
      EnvironmentCategory.GARAGE || this.formEditEnvironment?.get("category").value ==
      EnvironmentCategory.COMMON_AREA
    ) {
      return true;
    }
    return false;
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.saveSuccess",
        "dictionary.fillAllFieldsToContinue"
      ])
      .subscribe((translation) => {
        this._lblSaveSuccess = translation["dictionary.saveSuccess"];
        this._lblCompileAllFields = translation["dictionary.fillAllFieldsToContinue"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * ngOnChanges initialize Form and subscribe if we are in edit mode
   */
  public async ngOnChanges() {
    this.listEnvironmentCategory = Object.values(EnvironmentCategory)
      .filter((category: any) => !isNaN(category.toString()))
      .map(function (val: number): EnvironmentCategoryReverse {
        const envObject = {
          key: val,
          value: EnvironmentCategory[val],
        };
        return envObject;
      });

    if (this.currentEnvironment) {
      if (
        this.currentEnvironment.customDescription.localeCompare(
          this.currentEnvironment.nRoom,
          undefined,
          { sensitivity: "accent" }
        ) !== 0
      ) {
        this.copyDescription = false;
      }
    }

    if (this.currentEnvironment) {
      this.getCategoriesForEditMode();
    }

    if (this.currentEnvironment || !this.isEditMode) {
      this.initializeForm();
      this.handleThousandths();
      this.subscribeChangeForm();
    }
  }

  /**
   * Save environment data when clicking the save button
   */
  public async onClickSaveEnvironment(): Promise<void> {
    if (this.formEditEnvironment.valid) {
      try {
        await this._loaderService.presentLoaderWaiting();

        if (!this.isEditMode && !this.currentEnvironment) {
          const environmentInserted: Environment = await this._environmentService
            .insertEnvironment(
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE,
              this._idArea,
              this.formEditEnvironment.get("category").value,
              this._environmentService.editEnvironment.value.idParent,
              this.formEditEnvironment.get("sub").value,
              this.formEditEnvironment.get("unit").value,
            )
            .toPromise();

          await this._environmentService.addedPermission(
            this._currentUser,
            environmentInserted.id
          );

          this.currentEnvironment = environmentInserted;

          //If new env is a room add permission to users that are associated with apartment
          if (this.currentEnvironment.category === EnvironmentCategory.ROOM && this.currentEnvironment.idParent !== -1) {
            const parentEnv = await this._environmentService
              .getEnvironments(null, this.currentEnvironment.idParent, false)
              .toPromise();

            if (parentEnv.data[0].category === EnvironmentCategory.APARTAMENT) {
              const allUsersInEnv = await this._permissionService.getUserEnvironment(this.currentEnvironment.idParent);
              const usersAssociatedToEnv = allUsersInEnv.filter((user) => {
                return (
                  user.idLevelProtection == LevelProtection.RESIDENT ||
                  user.idLevelProtection == LevelProtection.MAINTENANCE ||
                  user.idLevelProtection == LevelProtection.SYSTEM
                );
              });

              if (usersAssociatedToEnv.length > 0) {
                for (let associatedUser of usersAssociatedToEnv) {
                  await this._permissionService.handlePermissionUserEnvironment(
                    this.currentEnvironment.id,
                    associatedUser.id,
                    true
                  );
                }
              }
            }
          }
        }

        await this.updateEnvironment(this.formEditEnvironment.value);

        await this._loaderService.dismissLoaderWaiting();
        await this._alertService.presentAlertOk(this._lblSaveSuccess, "", "");
        this.resetEnvironment.emit()
        this._systemRouterService.navigateToDigitalPlaceManageEnvironments(this._idArea);
      } catch {
        await this._loaderService.dismissLoaderWaiting();
        await this._alertService.presentAlertSomethingWrong();
      }
    } else {
      await this._alertService.presentAlertOk(this._lblCompileAllFields, "", "");
    }
  }


  /**
   * Subscribe on change form
   */
  public subscribeChangeForm() {
    this.formEditEnvironment.controls[
      "customDescription"
    ].valueChanges.subscribe((value) => {
      if (this.copyDescription) {
        this.formEditEnvironment.get("nRoom").setValue(value.slice(0, 25));
      }
    });
    this.formEditEnvironment.controls["nRoom"].valueChanges.subscribe(
      (value) => {
        if (
          value.localeCompare(
            this.formEditEnvironment.get("customDescription").value,
            undefined,
            { sensitivity: "accent" }
          ) !== 0
        ) {
          this.copyDescription = false;
        }
      }
    );
  }

  /**
   * Update environment data
   * @param environmentForm form data used for update
   */
  private async updateEnvironment(environmentForm: any): Promise<void> {
    environmentForm.id = this.currentEnvironment.id;
    environmentForm.descriptionEnvironment = environmentForm.customDescription;
    environmentForm.thousandths = parseInt(environmentForm.thousandths);
    environmentForm.thousandthsHeating = parseInt(
      environmentForm.thousandthsHeating
    );

    await this._environmentService
      .updateEnvironment(environmentForm)
      .toPromise();
  }

  /**
   * ngOnDestroy
   */
  public ngOnDestroy(): void {
    this._translateSubscription?.unsubscribe();
    this.unsubscribeStoreSelect();
  }

  /**
   * Convert enum to string
   * @param value
   * @returns
   */
  public convertEnumToString(value) {
    return EnumStringConverter.environmentCategoryToStringEnum(
      value,
      this._translateService,
      false
    );
  }

  /**
   * Initialize form
   */
  private async initializeForm(): Promise<void> {
    if (this.currentEnvironment?.thousandths == -1) {
      this.currentEnvironment.thousandths = 0;
    }
    if (this.currentEnvironment?.thousandthsHeating == -1) {
      this.currentEnvironment.thousandthsHeating = 0;
    }

    this.formEditEnvironment = this._fb.group({
      customDescription: new UntypedFormControl("", Validators.required),
      nRoom: new UntypedFormControl("", Validators.required),
      category: new UntypedFormControl(
        this._environmentService.editEnvironment.value.category
      ),
      thousandths: new UntypedFormControl(0),
      thousandthsHeating: new UntypedFormControl(0),
      sub: new UntypedFormControl(""),
      unit: new UntypedFormControl("")
    });

    if (this.currentEnvironment) {
      this.formEditEnvironment
        .get("customDescription")
        .patchValue(this.currentEnvironment?.customDescription);
      this.formEditEnvironment
        .get("nRoom")
        .patchValue(this.currentEnvironment?.nRoom);
      this.formEditEnvironment
        .get("category")
        .patchValue(this.currentEnvironment?.category);
      this.formEditEnvironment
        .get("thousandths")
        .patchValue(this.currentEnvironment?.thousandths);
      this.formEditEnvironment
        .get("thousandthsHeating")
        .patchValue(this.currentEnvironment?.thousandthsHeating);
      this.formEditEnvironment
        .get("sub")
        .patchValue(this.currentEnvironment?.sub);
      this.formEditEnvironment.get("unit")
        .patchValue(this.currentEnvironment?.unit);
    }

    if (
      (this.envHasDevicesAndChildren || !this.currentEnvironment?.removable) &&
      this.currentEnvironment
    ) {
      this.disableCategory = true;
    }
  }

  /**
   * Load current user data
   */
  private loadCurrentUserData(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser)
      )
      .subscribe(async (currentUser: CurrentSessionData) => {
        this._currentUser = currentUser;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectServerSettingsSub?.unsubscribe();
    this._selectCurrentUserSub?.unsubscribe();
  }

  //#endregion

  /**
   * Get categories list when user is editing an existing environment
   */
  private getCategoriesForEditMode(): void {
    if (this.currentEnvironment?.category == EnvironmentCategory.ROOM) {
      this.listEnvironmentCategory = this.listEnvironmentCategory.filter(
        (listElement: EnvironmentCategoryReverse) => {
          return listElement.key === EnvironmentCategory.ROOM;
        }
      );
    } else {
      this.listEnvironmentCategory = this.listEnvironmentCategory.filter(
        (listElement: EnvironmentCategoryReverse) => {
          return (
            listElement.key === EnvironmentCategory.COMMON_AREA ||
            listElement.key === EnvironmentCategory.FLOOR ||
            listElement.key === EnvironmentCategory.ELAVATOR_FLOOR ||
            listElement.key === EnvironmentCategory.GARAGE ||
            listElement.key === EnvironmentCategory.APARTAMENT ||
            listElement.key === EnvironmentCategory.EXTERNAL_ACCESS ||
            listElement.key === EnvironmentCategory.STAIRS ||
            listElement.key === EnvironmentCategory.CONDOMINIUM
          );
        }
      );
      this.getEnvironmentConstraintsForDropdownMenu(this.currentEnvironment.idParent);
    }
  }

  /**
   * Get categories list when user is creating a new environment
   * @param env {idParent: number, category: number}
   */
  private getCategoriesForNewEnvironments(env: { [key: string]: number }): void {
    if (env.idParent !== -1 && env.category !== 0) {
      this.getEnvironmentConstraintsForDropdownMenu(env.idParent);
    } else if (env.idParent === -1 && env.category !== 0) {
      this.listEnvironmentCategory = this.listEnvironmentCategory.filter(
        (listElement: EnvironmentCategoryReverse) => {
          return (
            listElement.key === EnvironmentCategory.ROOM ||
            listElement.key === EnvironmentCategory.COMMON_AREA ||
            listElement.key === EnvironmentCategory.FLOOR ||
            listElement.key === EnvironmentCategory.ELAVATOR_FLOOR ||
            listElement.key === EnvironmentCategory.GARAGE ||
            listElement.key === EnvironmentCategory.APARTAMENT ||
            listElement.key === EnvironmentCategory.EXTERNAL_ACCESS ||
            listElement.key === EnvironmentCategory.STAIRS ||
            listElement.key === EnvironmentCategory.CONDOMINIUM
          );
        }
      );
    } else {
      //If user refreshes the page when creating a new user return to manage environments page because
      // we don't have the value of the subscription anymore
      this._systemRouterService.navigateToDigitalPlaceManageEnvironments(this._idArea);
    }
    this.formEditEnvironment.get("category").patchValue(env.category);
  }
}
